<script setup lang="ts">
	const stages = [
		{
			stageNumber: '1',
			title: 'Клиент',
			description:
				'Клиент обращается к нам для заказа авиаперевозки товаров из Китая. Обсуждаются детали груза: объёмы, вес, сроки и особенности доставки',
			imgSrc: '/images/stage1.png',
			imgAlt: 'Человек с телефоном',
			imgSrcAfter: '/images/svg/after-stage1.svg',
		},
		{
			stageNumber: '2',
			title: 'Компания',
			description:
				'Мы предоставляет клиенту информацию о своих услугах, ценах и условиях. Подписывается договор, в котором прописаны все условия перевозки',
			imgSrc: '/images/stage2.png',
			imgAlt: 'Сотрудники компании выходят из офисного здания',
			imgSrcAfter: '/images/svg/after-stage2.svg',
		},
		{
			stageNumber: '3',
			title: 'Производитель',
			description:
				'Мы связываемся с производителем в Китае для организации отгрузки. Согласовываются сроки и условия доставки товара с производителем',
			imgSrc: '/images/stage3.png',
			imgAlt: 'Девушка ведет учет товаров на складе',
			imgSrcAfter: '/images/svg/after-stage3.svg',
		},
		{
			stageNumber: '4',
			title: 'Склад в Китае',
			description:
				'Товар поступает на склад в Китае, где проводится его проверка и оформляются необходимые документы. Производится упаковка и подготовка к авиаперевозке',
			imgSrc: '/images/stage4.png',
			imgAlt: 'Погрузчик на большом складе',
			imgSrcAfter: '/images/svg/after-stage4.svg',
		},
		{
			stageNumber: '5',
			title: 'Авиаперевозка',
			description:
				'Товар загружается на транспортное средство и доставляется в аэропорт. Проходят процедуры таможенного оформления для вывоза груза из Китая',
			imgSrc: '/images/stage5.png',
			imgAlt: 'Летящий из Китая самолёт',
			imgSrcAfter: '/images/svg/after-stage5.svg',
		},
		{
			stageNumber: '6',
			title: 'Склад в России',
			description:
				'Товар прибывает в Россию, где также проходит таможенное оформление для ввоза. Груз транспортируется на склад в России, где проводится его проверка ',
			imgSrc: '/images/stage6.png',
			imgAlt: 'Большие складские помещения с товаром',
			imgSrcAfter: '/images/svg/after-stage6.svg',
		},
		{
			stageNumber: '7',
			title: 'Доставка / Самовывоз',
			description:
				'Курьерская доставка по РФ или самовывоз со склада. Организуется доставка по выбранному маршруту или определяются условия для самовывоза.',
			imgSrc: '/images/stage7.png',
			imgAlt: 'Грузовик везет товар клиенту',
			imgSrcAfter: '/images/svg/after-stage7.svg',
		},
		{
			stageNumber: '8',
			title: 'Товар получен клиентом ',
			description:
				'Клиент получает товар, подтверждает его соответствие условиям договора. Происходит оформление всех необходимых документов',
			imgSrc: '/images/stage8.png',
			imgAlt: 'Много коробок с продукцией у клиента',
			imgSrcAfter: '/images/svg/after-stage8.svg',
		},
	];

	const { isVisible, animatedCardsRefs } = useAnimationAppearance({
		delay: 500,
		rootMargin: '20px',
	});
</script>

<template>
	<section id="stage" class="stages-section" itemscope itemtype="https://schema.org/HowTo">
		<h2 class="stages-section__title" itemprop="name">Этапы работы</h2>
		<ul class="stages-section__content-container">
			<li
				v-for="(stage, index) in stages"
				:key="stage.stageNumber"
				:ref="(el) => (animatedCardsRefs[index] = el)"
				class="stages-section__list-item"
				:class="{ 'is-visible': isVisible[index] }"
				itemprop="step"
				itemscope
				itemtype="https://schema.org/HowToStep"
			>
				<div class="item__text-content">
					<div class="item__header">
						<span>{{ stage.stageNumber }}</span>
						<h3 class="item__title" itemprop="name">
							{{ stage.title }}
						</h3>
					</div>
					<div class="item__description" itemprop="text">
						{{ stage.description }}
					</div>
				</div>
				<NuxtImg
					:src="stage.imgSrc"
					:alt="stage.imgAlt"
					densities="x1 x2"
					format="webp"
					sizes="360px xs:600px md:1200px xl:360px"
					loading="lazy"
					class="item__image"
				/>
				<NuxtImg
					:src="stage.imgSrcAfter"
					aria-hidden="true"
					loading="lazy"
					:class="{
						'item__image-path-left': !!(index % 2),
						'item__image-path-right': !(index % 2),
						'item__image-path-last': index === 7,
					}"
				/>
				<NuxtImg
					v-if="index === 7"
					src="/images/svg/telegram-left.svg"
					aria-hidden="true"
					loading="lazy"
					class="item-plane__image-path-last"
				/>
			</li>
		</ul>
	</section>
</template>

<style lang="scss" scoped>
	.stages-section {
		padding-bottom: 60px;
	}
	.stages-section__title {
		font-size: var(--font-size-title);
		color: var(--color-tertiary);
		font-weight: 700;
		margin-bottom: 15px;
	}
	.stages-section__content-container {
		display: grid;
		grid-template-columns: 1fr;
		gap: 51px;
	}
	.stages-section__list-item {
		position: relative;
		display: flex;
		flex-direction: column;
		gap: 8px;
		background-color: var(--color-white);
		box-shadow: var(--box-shadow);
		border-radius: 32px;
		padding: 15px;
		transform: scale(0.6);
		opacity: 0;
		transition: transform ease-in 0.5s, opacity ease-in 0.5s;
		&.is-visible {
			transform: scale(1);
			opacity: 1;
		}
		& .item__image-path-right {
			display: none;
		}
		& .item__image-path-left {
			display: none;
		}
		& .item__image-path-last {
			display: none;
		}
		& .item-plane__image-path-last {
			display: none;
		}
		&:nth-child(2n + 1):after {
			position: absolute;
			z-index: -1;
			content: '';
			width: 255px;
			height: 51px;
			bottom: 0;
			left: 50%;
			transform: translate(-50%, 100%);
			background-image: url('/images/svg/after-stage1-mobile.svg');
			background-size: contain;
		}
		&:nth-child(2n):after {
			position: absolute;
			z-index: -1;
			content: '';
			width: 255px;
			height: 51px;
			bottom: 0;
			left: 50%;
			transform: translate(-50%, 100%);
			background-image: url('/images/svg/after-stage2-mobile.svg');
			background-size: contain;
		}
		&:last-child:after {
			position: absolute;
			z-index: -1;
			content: '';
			width: 255px;
			height: 80px;
			bottom: 0;
			left: 50%;
			transform: translate(-50%, 100%);
			background-image: url('/images/svg/after-stage-last-mobile.svg');
			background-size: cover;
		}
	}
	.item__text-content {
		padding: 0px;
	}
	.item__header {
		display: inline-flex;
		align-items: center;
		margin-bottom: 8px;

		.item__title {
			font-size: 20px;
			font-weight: 700;
		}

		span {
			display: inline-flex;
			justify-content: center;
			align-items: center;
			width: 26px;
			height: 26px;
			border-radius: 50%;
			font-size: 16px;
			font-weight: 700;
			color: var(--color-white);
			background-color: var(--color-primary);
			margin-right: 10px;
		}
	}
	.item__description {
		text-wrap: balance;
	}
	.item__image {
		width: 100%;
		aspect-ratio: 2.8;
		border-radius: 20px;
		object-fit: cover;
	}

	@media (min-width: 1240px) {
		.stages-section {
			padding-bottom: 124px;
		}
		.stages-section__title {
			margin-bottom: 20px;
		}
		.stages-section__content-container {
			grid-template-columns: 1fr 1fr;
			gap: 77px 24px;
		}
		.stages-section__list-item {
			position: relative;
			display: flex;
			flex-direction: row;
			gap: 24px;
			background-color: var(--color-white);
			box-shadow: var(--box-shadow);
			border-radius: 32px;
			padding: 12px;
			&:nth-child(2n) {
				top: 124px;
			}
			& .item__image-path-right {
				display: block;
				z-index: -1;
				position: absolute;
				right: 0px;
				bottom: 47px;
				transform: translateX(100%);
			}
			& .item__image-path-left {
				display: block;
				z-index: -1;
				position: absolute;
				left: 0px;
				bottom: 47px;
				transform: translateX(-100%);
			}
			& .item__image-path-last {
				display: block;
				z-index: -1;
				position: absolute;
				right: 204px;
				left: auto;
				top: auto;
				bottom: -38px;
				transform: translateX(0%);
			}
			& .item-plane__image-path-last {
				display: flex;
				position: absolute;
				content: '';
				top: 110px;
				left: -235px;
			}
			&:nth-child(2n + 1):after {
				display: none;
			}
			&:nth-child(2n):after {
				display: none;
			}
			&:last-child:after {
				display: none;
			}
		}
		.item__text-content {
			padding: 8px 0px 8px 8px;
		}
		.item__header {
			display: inline-flex;
			align-items: center;
			margin-bottom: 12px;

			.item__title {
				font-size: 24px;
				font-weight: 700;
			}

			span {
				display: inline-flex;
				justify-content: center;
				align-items: center;
				width: 31px;
				height: 31px;
				border-radius: 50%;
				font-size: 20px;
				font-weight: 700;
				color: var(--color-white);
				background-color: var(--color-primary);
				margin-right: 12px;
			}
		}
		.item__description {
			text-wrap: balance;
		}
		.item__image {
			flex: 0 0 auto;
			width: 168px;
			height: 147px;
			aspect-ratio: unset;
			border-radius: 20px;
			object-fit: cover;
		}
	}
</style>
